import {EnumDefinition} from 'src/js/containers/enum';

export type HierarchyNode<T> = T & {
  children: HierarchyNode<T>[];
};
export type GetHierarchyIds<T = any, K = any> = (option: T) => {id: K; parentId: K | undefined | null};
export type HierarchyMetadata = {
  /** `true` if `(children.length > 0)` */
  hasChildren?: boolean;
  /** `true` if `(rootNodes.length == 1) && ((isTheRootNode && children.length > 0) || children.length == 1)` \
   * stopping early if `(children.length != 1)`
   */
  isSoloRootNode?: boolean;
};
export const ArrayUtils = {
  makeHierarchy<T, K extends string | number>(options: T[], getHierarchyIds: GetHierarchyIds<T, K>): HierarchyNode<T>[] {
    // NOTE: backend can send us a partial tree, without the parents
    const validIds = {} as Record<K, boolean>;
    options.forEach((option) => {
      const {id} = getHierarchyIds(option);
      validIds[id] = true;
    });

    const idToOption = {} as Record<K, T>;
    const idToChildIds = {} as Record<K, K[]>;
    const rootNodeIds: K[] = [];
    options.forEach((option) => {
      // idToOption
      const hierarchyIds = getHierarchyIds(option);
      const {id} = hierarchyIds;
      idToOption[id] = option;

      let {parentId} = hierarchyIds;
      if (!validIds[parentId as K]) parentId = null;

      if (parentId == null) {
        // rootNodeIds
        rootNodeIds.push(id);
      } else {
        // idToChildIds
        if (!(parentId in idToChildIds)) {
          idToChildIds[parentId] = [];
        }
        idToChildIds[parentId].push(id);
      }
    });
    const mapNodeInfos = (id: K): HierarchyNode<T> => {
      const childIds = idToChildIds[id] as K[] | undefined;
      return {...idToOption[id], children: childIds?.length ? childIds.map(mapNodeInfos) : []};
    };
    return rootNodeIds.map(mapNodeInfos);
  },
  computeHierarchyMetadata<T>(options: T[], getHierarchyIds: GetHierarchyIds<T>): (T & HierarchyMetadata)[] {
    // hasChildren
    const hasChildren_set = new Set<string | number>();
    options.forEach((option) => {
      const {parentId} = getHierarchyIds(option);
      if (parentId) hasChildren_set.add(parentId);
    });
    // isSoloRootNode
    const isSoloRootNode_set = new Set<string | number>();
    const hierarchy = ArrayUtils.makeHierarchy(options, getHierarchyIds);
    if (hierarchy.length === 1) {
      const rootNode = hierarchy[0];
      ArrayUtils.walkHierarchy(hierarchy, (node) => {
        const {id} = getHierarchyIds(node);
        const isSoloRootNode = (node === rootNode && node.children.length > 0) || node.children.length === 1;
        if (isSoloRootNode) isSoloRootNode_set.add(id);
        return node.children.length === 1; // NOTE: we don't have to stop early here, but we will for consistent grouping
      });
    }
    // return
    return options.map((option) => {
      const {id} = getHierarchyIds(option);
      return {
        ...option,
        hasChildren: hasChildren_set.has(id),
        isSoloRootNode: isSoloRootNode_set.has(id),
      };
    });
  },
  walkHierarchy<T>(nodes: HierarchyNode<T>[], callback: (v: HierarchyNode<T>) => boolean | void) {
    const walkNode = (node: HierarchyNode<T>) => {
      const shouldContinue = callback(node) ?? true;
      if (shouldContinue) {
        node.children?.forEach(walkNode);
      }
    };
    nodes.forEach(walkNode);
  },
  flattenNodes<T>(nodes: HierarchyNode<T>[]) {
    const options: HierarchyNode<T>[] = [];
    const walkNodes = (node: HierarchyNode<T>) => {
      options.push(node);
      node.children?.forEach(walkNodes);
    };
    nodes.forEach(walkNodes);
    return options;
  },
  /* eslint-disable no-param-reassign */
  /**
   * Insert item into array on specific insert position.
   * All items after this index are shifted right.
   *
   * @param {Array} arr The array, which is inserted into.
   * @param insertIndex Index position in array where the item is inserted.
   * @param insertedItem The inserted item.
   */
  insert<T extends Record<string, any>>(arr: T[], insertIndex: number, insertedItem: T) {
    arr.length += 1;
    const len: number = arr.length;
    let idx: number = len - 1;

    while (idx > insertIndex) {
      arr[idx] = arr[idx - 1];
      idx -= 1;
    }
    arr[insertIndex] = insertedItem;
  },
  /* eslint-enable no-param-reassign */
  /**
   * Return a copy of the array, sorted by an array of keys, optionally in reverse
   *
   * @param array The array to sort
   * @param by Keys to sort by
   * @param reverse Which keys should be reversed
   */
  sortedBy<T, K>(array: Array<T> | null | undefined, by: (value: T) => K[], reverse: boolean[] = []) {
    return [...(array ?? [])].sort((a, b) => {
      const keyA = by(a);
      const keyB = by(b);
      const n = Math.max(keyA.length, keyB.length);
      for (let i = 0; i < n; i++) {
        const diff = +(keyA?.[i] > keyB?.[i]) - +(keyA?.[i] < keyB?.[i]);
        if (diff !== 0) {
          return reverse?.[i] ? -diff : diff;
        }
      }
      return 0;
    });
  },
  sortedByLocaleString<T>(array: T[], key: (value: T) => string, locale: string, options?: Intl.CollatorOptions): T[] {
    const {compare} = new Intl.Collator(locale, options);
    return [...array].sort((a, b) => {
      return compare(key(a), key(b));
    });
  },
  reversed<T>(array: Array<T> | null | undefined) {
    const copy = [...(array ?? [])];
    copy.reverse();
    return copy;
  },
  zip<A, B>(arr1: A[] | undefined | null, arr2: B[] | undefined | null): [A | undefined, B | undefined][] {
    const length = Math.max(arr1?.length ?? 0, arr2?.length ?? 0);
    return Array(length)
      .fill(0)
      .map((_, i) => [arr1?.[i], arr2?.[i]]);
  },
  zipAll<T>(...arrays: T[][]): (T | undefined)[][] {
    const length = arrays.reduce((acc, v) => Math.max(acc, v.length), 0);
    return Array(length)
      .fill(0)
      .map((_, i) =>
        Array(arrays.length)
          .fill(0)
          .map((_, j) => arrays[j][i])
      );
  },
  sum<T>(array: T[] | null | undefined, key: (value: T) => number): number {
    return (array ?? []).reduce((acc, v) => acc + key(v), 0);
  },
  average<T>(array: T[], key: (v: T) => number): number {
    return array.reduce((acc, v) => acc + key(v), 0) / (array.length || 1);
  },
  asArray<T>(arrayOrValueOrNullsy: T | T[] | null | undefined): T[] {
    if (Array.isArray(arrayOrValueOrNullsy)) return arrayOrValueOrNullsy;
    return arrayOrValueOrNullsy == null ? [] : [arrayOrValueOrNullsy];
  },
  /* enum to boolean */
  _getEnumYesNo(enumList: EnumDefinition[]): {YES_VALUE: EnumDefinition | undefined; NO_VALUE: EnumDefinition | undefined} {
    const YES_VALUE = enumList.find((v) => v.name === 'YES' || v.name === 'yes'); // NOTE: fake FE enums have 'yes' (like ATB susceptibility)
    const NO_VALUE = enumList.find((v) => v !== YES_VALUE);
    return {YES_VALUE, NO_VALUE};
  },
  mapBooleanToEnum(
    booleanArray: boolean[] | undefined | null,
    enumList: EnumDefinition[],
    sortKey: (enumValueName: string) => string
  ): string[] | undefined | null {
    if (!booleanArray) return booleanArray;
    const {YES_VALUE, NO_VALUE} = ArrayUtils._getEnumYesNo(enumList);
    const enumArray = booleanArray.map((v) => {
      const enumDefinition = [NO_VALUE, YES_VALUE][+v];
      return enumDefinition?.name ?? String(v);
    });
    return ArrayUtils.sortedBy(enumArray, (v) => [sortKey(v)]);
  },
  mapEnumToBoolean(enumNameArray: string[] | undefined | null, enumList: EnumDefinition[]): boolean[] | undefined | null {
    if (!enumNameArray) return enumNameArray;
    const {YES_VALUE} = ArrayUtils._getEnumYesNo(enumList);
    return enumNameArray.map((v) => v === YES_VALUE?.name);
  },
  mapSingleBooleanToEnum(boolValue: boolean | undefined | null, enumList: EnumDefinition[]): string | undefined | null {
    if (boolValue == null) return boolValue;
    const {YES_VALUE, NO_VALUE} = ArrayUtils._getEnumYesNo(enumList);
    const enumDefinition = [NO_VALUE, YES_VALUE][+boolValue];
    return enumDefinition?.name ?? String(boolValue);
  },
  mapSingleEnumToBoolean(enumValueName: string | undefined | null, enumList: EnumDefinition[]): boolean | undefined | null {
    if (enumValueName == null) return enumValueName;
    const {YES_VALUE} = ArrayUtils._getEnumYesNo(enumList);
    return enumValueName === YES_VALUE?.name;
  },
};
